import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import {
    useDraggableCard,
    DRAGGABLE_CARD_ANIMATION,
    DRAGGABLE_CARD_CLASSES,
    DEFAULT_CARD_WIDTH
} from "@/hooks/useDraggableCard";
import { IconFrame } from "@scripts/react/components/IconFrame";

const TIPS = [
    {
        icon: "fad fa-bullseye-arrow",
        title: "Be Specific and Concise",
        description: `Short, specific noun phrases work best. Instead of "car," try "yellow school bus".`
    },
    {
        icon: "fad fa-text",
        title: "Iterate and Refine",
        description: `If one prompt doesn't work, try alternatives. For example, "person" vs "human" vs "pedestrian" may yield different results.`
    },
    {
        icon: "fad fa-list",
        title: "Use Common Terms",
        description: `Simple, commonly-used words work best. Avoid jargon or overly technical terms.`
    }
];

interface PromptingTipsCardProps {
    isOpen: boolean;
    onClose: () => void;
    anchorElement?: HTMLElement | null;
}

export function PromptingTipsCard({ isOpen, onClose, anchorElement }: PromptingTipsCardProps) {
    const { position, cardRef, startDrag } = useDraggableCard({ isOpen, anchorElement });

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={cardRef}
                    className={twClassNames(DRAGGABLE_CARD_CLASSES, "p-4")}
                    style={{ left: position.left, top: position.top, width: DEFAULT_CARD_WIDTH }}
                    onMouseDown={startDrag}
                    {...DRAGGABLE_CARD_ANIMATION}
                >
                    <div className="flex flex-1 flex-col gap-5 overflow-y-auto">
                        {TIPS.map((tip, index) => (
                            <motion.div
                                key={index}
                                className="flex flex-col gap-3"
                                initial={{ opacity: 0, y: 8, filter: "blur(5px)" }}
                                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                transition={{ duration: 0.3, ease: "easeOut", delay: index * 0.1 }}
                            >
                                <div className="flex gap-3">
                                    {/* @ts-expect-error - IconFrame component type issue */}
                                    <IconFrame icon={tip.icon} size="md" color="gray" />
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="text-sm font-semibold text-primary">
                                            {tip.title}
                                        </h3>
                                        <p className="text-xs leading-relaxed text-tertiary">
                                            {tip.description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* @ts-expect-error - HoverDeleteButton type mismatch with JSX.Element */}
                    <HoverDeleteButton
                        onClick={onClose}
                        ariaLabel="Dismiss prompting tips"
                        className="-right-1 -top-1"
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}
